// src/components/Dashboard/FieldVisitsWidget.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import visitReportsService from '../../services/visitReports.service';
import './FieldVisitsWidget.css';

// Current financial year (Apr–Mar)
const getCurrentFY = () => {
  const now = new Date();
  const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return {
    fromDate: `${year}-04-01`,
    toDate:   `${year + 1}-03-31`,
  };
};

const FieldVisitsWidget = () => {
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [data, setData]       = useState({ employeeSummary: [], totals: {} });

  useEffect(() => {
    const fy = getCurrentFY();
    const params = { fromDate: fy.fromDate, toDate: fy.toDate };

    // ✅ Pass companyId for SA / OA
    if (isSuperAdmin) {
      const cid = localStorage.getItem('selectedCompanyId');
      if (cid) params.companyId = cid;
    }

    visitReportsService.getSummary(params)
      .then(res => {
        if (res.success) setData(res.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isSuperAdmin]);

  const topFive = (data.employeeSummary || []).slice(0, 5);
  const totals  = data.totals || {};

  return (
    <div className="fvw-container">
      <div className="fvw-header">
        <div>
          <h6 className="fvw-title">📊 Field Visits</h6>
          <p className="fvw-subtitle">This financial year</p>
        </div>
        <button className="fvw-view-all" onClick={() => navigate('/reports/field-visits')}>
          View All →
        </button>
      </div>

      {loading ? (
        <div className="fvw-loading">Loading visits data…</div>
      ) : (
        <>
          {/* Totals strip */}
          <div className="fvw-totals">
            <div className="fvw-total-item">
              <span className="fvw-total-dot" style={{ background: '#2e7d32' }} />
              <div>
                <p className="fvw-total-value">{totals.firstVisits || 0}</p>
                <p className="fvw-total-label">First</p>
              </div>
            </div>
            <div className="fvw-total-item">
              <span className="fvw-total-dot" style={{ background: '#1976d2' }} />
              <div>
                <p className="fvw-total-value">{totals.followupVisits || 0}</p>
                <p className="fvw-total-label">Followup</p>
              </div>
            </div>
            <div className="fvw-total-item">
              <span className="fvw-total-dot" style={{ background: '#f57c00' }} />
              <div>
                <p className="fvw-total-value">{totals.dealerVisits || 0}</p>
                <p className="fvw-total-label">Dealer</p>
              </div>
            </div>
          </div>

          {/* Top 5 employees chart */}
          {topFive.length > 0 ? (
            <div className="fvw-chart">
              <p className="fvw-section-label">Top Performers</p>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart
                  data={topFive}
                  layout="vertical"
                  margin={{ top: 5, right: 15, left: 0, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis
                    type="category"
                    dataKey="employeeName"
                    tick={{ fontSize: 11, fill: '#5a6878' }}
                    width={100}
                  />
                  <Tooltip />
                  <Bar dataKey="totalVisits" fill="#2e7d32" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="fvw-empty">
              <p>No visit data yet</p>
            </div>
          )}

          {/* Top 5 list */}
          {topFive.length > 0 && (
            <div className="fvw-list">
              {topFive.map((e, i) => (
                <div key={e.employeeId} className="fvw-list-item">
                  <span className="fvw-rank">#{i + 1}</span>
                  <div className="fvw-list-body">
                    <span className="fvw-list-name">{e.employeeName}</span>
                    <span className="fvw-list-role">{e.roleName}</span>
                  </div>
                  <div className="fvw-list-counts">
                    <span className="fvw-count-first" title="First visits">{e.firstVisits}</span>
                    <span className="fvw-count-follow" title="Followup visits">{e.followupVisits}</span>
                    <span className="fvw-count-dealer" title="Dealer visits">{e.dealerVisits}</span>
                    <span className="fvw-count-total">{e.totalVisits}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FieldVisitsWidget;